import React, { useCallback } from 'react'
import Card from '@/layout/card'
import { Form, Input, Image, message, Spin, Popover, Radio } from 'antd'
import {
  BranchesOutlined,
  CheckCircleFilled,
  CheckCircleOutlined,
  CloseOutlined,
  DownOutlined,
  EditOutlined,
  FileAddOutlined,
  FileImageOutlined,
  GlobalOutlined,
  SettingOutlined
} from '@ant-design/icons'
import styles from './index.module.scss'
import penSvg from '@/assets/images/magic-pen.svg'
import genVideoSvg from '@/assets/images/gen-video.svg'
import Button from '@/components/button'
import LottieLoading from '@/components/lottie-loading'
import finishSvg from '@/assets/images/card-task-finish.png'
import loadingSvg from '@/assets/images/card-task-loading.svg'
import { CommonMsgNodeProps } from '@/types/message'
import cn from 'classnames'
import useNodeSubmitDisabledStatus from '@/hooks/use-node-disabled'
import { observer } from 'mobx-react'
import { createVideoAPI } from '@/proxy-request/api'
import type {
  VideoCreateRatio,
  VideoCreateScriptMode,
  VideoProductImagesItem
} from '@/proxy-request/api'
import { reGeneDescCreativeAPI, regeneImageAPI, reuploadImageAPI } from '@/servers/agent-v2'
import SkeletonScreen from './skeleton-screen'
import useMessageUpdateFlag from '@/hooks/use-message-update-flag'
import { debounce, isPlainObject } from 'lodash'
import { saveActionRecordToServer } from '@/proxy-request/help'
import { safeParseJson } from '@/utils'
import { CosUpload } from '@/components/upload'
import { saveUserTextToChatPanel } from '@/proxy-request/chat-completion'

const CREATIVE_PLACEHOLDER = '请输入脚本的创意描述'
const MAX_REFERENCE_IMAGES = 9
const UPLOAD_MESSAGE_KEY = 'video-desc-card-upload'

type AspectRatio = Exclude<VideoCreateRatio, '11:1'>
type PopoverKey = 'spec' | 'language' | 'quantity'
type UpdateSettingsOptions = {
  source?: 'user' | 'server'
}
type VideoDescImageItem = {
  image_url: string
  has_face?: boolean
}

interface VideoSettings {
  ratio: AspectRatio
  duration: number
  language: string
  count: number
  script_mode: VideoCreateScriptMode
}

const ASPECT_RATIO_OPTIONS: Array<{
  value: AspectRatio
  label: string
  width: number
  height: number
}> = [
  { value: '9:16', label: '9:16', width: 720, height: 1280 },
  { value: '16:9', label: '16:9', width: 1280, height: 720 },
  { value: '1:1', label: '1:1', width: 1024, height: 1024 },
  { value: '4:3', label: '4:3', width: 1024, height: 768 }
]

const DURATION_OPTIONS = [5, 10, 15]
const DEFAULT_DURATION = 15

/** 美国-英语、泰国-泰语、越南-越南语、马来西亚-马来语、日本-日语；另含中文 */
const VIDEO_LANGUAGE_OPTIONS = [
  { value: 'en', label: '英语' },
  { value: 'th', label: '泰语' },
  { value: 'vi', label: '越南语' },
  { value: 'ja', label: '日语' },
  { value: 'ms', label: '马来语' },
  { value: 'zh-CN', label: '中文' },
  { value: 'fil', label: '菲律宾语' },
  { value: 'id', label: '印尼语' },
  { value: 'es', label: '西班牙语' }
] as const

const COUNTRY_LANGUAGE_MAP: Record<string, string> = {
  US: 'en',
  GB: 'en',
  TH: 'th',
  VN: 'vi',
  MY: 'ms',
  JP: 'ja',
  CN: 'zh-CN',
  PH: 'fil',
  ID: 'id',
  ES: 'es',
  MX: 'es'
}

const SCRIPT_TYPE_OPTIONS: Array<{ value: VideoCreateScriptMode; label: string }> = [
  { value: 'variant_script', label: '变体脚本' },
  { value: 'same_script', label: '相同脚本' }
]

const QUANTITY_OPTIONS = [1, 2, 3, 4, 5, 6]

const DEFAULT_SETTINGS: VideoSettings = {
  ratio: '9:16',
  duration: DEFAULT_DURATION,
  language: 'en',
  count: 1,
  script_mode: 'variant_script'
}

interface VideoDescCardProps extends CommonMsgNodeProps {
  node: {
    value: {
      ext_data?: string | null
      desc: string
      images?: VideoDescImageItem[]
      title: string
      disabled?: boolean
      status: number // 1生成中 2 生成已完成 3 失败
      language?: string
    }
  }
}

const statusMap = {
  0: loadingSvg,
  1: loadingSvg,
  2: finishSvg,
  3: finishSvg
}

const maxCreativeLen = 2500

const normalizeImageUrls = (value?: string | string[]) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean).slice(0, MAX_REFERENCE_IMAGES)
  }
  return value ? [value].slice(0, MAX_REFERENCE_IMAGES) : []
}

const normalizeVideoDescImages = (value?: VideoDescImageItem[]) => {
  const images = Array.isArray(value)
    ? value.filter(item => item?.image_url).slice(0, MAX_REFERENCE_IMAGES)
    : []
  const imageUrls = images.map(item => item.image_url)
  const faceMap = images.reduce<Record<string, boolean>>((map, item) => {
    map[item.image_url] = Boolean(item.has_face)
    return map
  }, {})

  return {
    imageUrls,
    faceMap,
    hasFace: images.some(item => Boolean(item.has_face))
  }
}

const normalizeAspectRatio = (value: unknown): AspectRatio => {
  return ASPECT_RATIO_OPTIONS.some(item => item.value === value)
    ? (value as AspectRatio)
    : DEFAULT_SETTINGS.ratio
}

const normalizeScriptType = (value: unknown): VideoCreateScriptMode => {
  return SCRIPT_TYPE_OPTIONS.some(item => item.value === value)
    ? (value as VideoCreateScriptMode)
    : DEFAULT_SETTINGS.script_mode
}

const normalizeCount = (value: unknown) => {
  const count = Number(value)
  return QUANTITY_OPTIONS.includes(count) ? count : DEFAULT_SETTINGS.count
}

const normalizeDuration = (value: unknown) => {
  const duration = Number(value)
  return DURATION_OPTIONS.includes(duration) ? duration : DEFAULT_DURATION
}

const getLanguageLabel = (value: string) => {
  return VIDEO_LANGUAGE_OPTIONS.find(item => item.value === value)?.label ?? (value || '英语')
}

const getDefaultLanguageByCountry = (country?: unknown) => {
  if (typeof country !== 'string') {
    return DEFAULT_SETTINGS.language
  }
  return COUNTRY_LANGUAGE_MAP[country.toUpperCase()] ?? DEFAULT_SETTINGS.language
}

const getNonEmptyString = (value: unknown) => {
  return typeof value === 'string' && value.trim() ? value.trim() : ''
}

const getExtDataDefaultLanguage = (extData?: Record<string, unknown>, fallback?: string) => {
  const fallbackLanguage = getNonEmptyString(fallback)
  const extDataLanguage = getNonEmptyString(extData?.language)

  if (extDataLanguage) {
    return extDataLanguage
  }
  if (fallbackLanguage) {
    return fallbackLanguage
  }
  return getDefaultLanguageByCountry(
    extData?.sales_region ?? extData?.salesRegion ?? extData?.country ?? extData?.region
  )
}

const getVideoProductImages = (
  imageUrls: string[],
  faceMap: Record<string, boolean>
): VideoProductImagesItem[] => {
  return imageUrls.map(imageUrl => ({
    image_url: imageUrl,
    has_face: Boolean(faceMap[imageUrl])
  }))
}

const getHasFace = (urls: string[], faceMap: Record<string, boolean>) => {
  return urls.some(url => Boolean(faceMap[url]))
}

const getMessageUpdateLanguage = ({
  currentLanguage,
  nextLanguage,
  languageChangedByUser
}: {
  currentLanguage: string
  nextLanguage?: string
  languageChangedByUser: boolean
}) => {
  const normalizedNextLanguage = getNonEmptyString(nextLanguage)

  if (!normalizedNextLanguage || languageChangedByUser) {
    return undefined
  }

  return currentLanguage === normalizedNextLanguage ? undefined : normalizedNextLanguage
}

const isRecord = (value: unknown): value is Record<string, unknown> => isPlainObject(value)

const VideoDescCard = (props: VideoDescCardProps) => {
  const [form] = Form.useForm()
  const { status = 2, title = '视频设置', desc = '', language, images } = props.node?.value || {}
  const initialImages = normalizeVideoDescImages(images)
  const [videoDesc, setVideoDesc] = React.useState(desc)
  const [imageUrlList, setImageUrlList] = React.useState(() => initialImages.imageUrls)
  const [imageFaceMap, setImageFaceMap] = React.useState<Record<string, boolean>>(
    () => initialImages.faceMap
  )
  const [deleteHoverIndex, setDeleteHoverIndex] = React.useState<number | null>(null)
  /** 上传回调会并发/连续触发，ref 用来在异步闭包里读取最新列表，避免 state 延迟导致重复处理 */
  const imageUrlListRef = React.useRef<string[]>(imageUrlList)
  /** 记录 CosUpload 上一次 onChange 的累计 URL 列表，用差集找出本次新增图片 */
  const uploadValueListRef = React.useRef<string[]>(imageUrlList)
  /** 记录正在 reupload/检测人脸的 URL，防止连续回调时同一 URL 重复请求 */
  const pendingUploadUrlSetRef = React.useRef<Set<string>>(new Set())
  const [hasFace, setHasFace] = React.useState(() => initialImages.hasFace)
  const [settings, setSettings] = React.useState<VideoSettings>({
    ...DEFAULT_SETTINGS,
    language: language || DEFAULT_SETTINGS.language
  })
  const [openPopover, setOpenPopover] = React.useState<PopoverKey | null>(null)
  const [optimizeLoading, setOptimizeLoading] = React.useState(false)
  const [customLanguageInput, setCustomLanguageInput] = React.useState('')
  const languageChangedByUserRef = React.useRef(false)

  const { submitDisabled, setSubmitDisabled } = useNodeSubmitDisabledStatus({
    messageId: props?.msgContent?.message_id || '',
    initialDisabled: props?.node?.value?.disabled || false
  })

  React.useEffect(() => {
    form.setFieldsValue(settings)
  }, [])

  React.useEffect(() => {
    const value = props.node?.value
    const extData = value?.ext_data
      ? (safeParseJson(value.ext_data || '') as Record<string, unknown>)
      : undefined
    let nextSettings: VideoSettings = {
      ...DEFAULT_SETTINGS,
      language: getExtDataDefaultLanguage(isRecord(extData) ? extData : undefined, language)
    }

    if (isRecord(extData)) {
      nextSettings = {
        ratio: normalizeAspectRatio(extData.ratio),
        duration: normalizeDuration(extData.duration),
        language: getExtDataDefaultLanguage(extData, language),
        count: normalizeCount(extData.count),
        script_mode: normalizeScriptType(extData.script_mode)
      }
    }

    setSettings(nextSettings)
    setCustomLanguageInput(
      VIDEO_LANGUAGE_OPTIONS.some(item => item.value === nextSettings.language)
        ? ''
        : nextSettings.language
    )
    form.setFieldsValue(nextSettings)
  }, [])

  // 与 novice-mode 一致：useCallback 包一层 lodash debounce
  const debouncedOnValuesChange = useCallback(
    debounce((payload: { messageId: string; values: Record<string, unknown> }) => {
      void saveActionRecordToServer({
        message_id: payload.messageId,
        value: payload.values as Parameters<typeof saveActionRecordToServer>[0]['value']
      })
    }, 1000),
    []
  )

  useMessageUpdateFlag(props.msgContent.message_id || '', {
    onChange: () => {
      const v = props.node?.value
      const { language: nextLanguage } = props.node?.value || {}
      const nextImages = normalizeVideoDescImages(v?.images)
      setVideoDesc(v?.desc ?? '')
      setImageUrlList(nextImages.imageUrls)
      imageUrlListRef.current = nextImages.imageUrls
      uploadValueListRef.current = nextImages.imageUrls
      setImageFaceMap(nextImages.faceMap)
      setHasFace(nextImages.hasFace)
      const syncedLanguage = getMessageUpdateLanguage({
        currentLanguage: settings.language,
        nextLanguage,
        languageChangedByUser: languageChangedByUserRef.current
      })

      if (syncedLanguage) {
        updateSettings({ language: syncedLanguage }, { source: 'server' })
      }
    }
  })

  const updateSettings = (partial: Partial<VideoSettings>, options: UpdateSettingsOptions = {}) => {
    if (partial.language !== undefined && options.source !== 'server') {
      languageChangedByUserRef.current = true
    }

    setSettings(prev => {
      const next: VideoSettings = {
        ...prev,
        ...partial,
        duration: normalizeDuration(partial.duration ?? prev.duration)
      }
      form.setFieldsValue(next)
      if (options.source !== 'server') {
        debouncedOnValuesChange({
          messageId: props?.msgContent?.message_id || '',
          values: next as unknown as Record<string, unknown>
        })
      }
      return next
    })
  }

  const onSubmit = async () => {
    if (submitDisabled) return
    if (imageUrlList.length === 0) {
      message.error('请上传参考图片')
      return
    }
    if (videoDesc.trim() === '') {
      message.error('请输入脚本的创意描述')
      return
    }
    await form.validateFields()
    setSubmitDisabled(true)

    createVideoAPI({
      body: {
        count: settings.count,
        product_images: getVideoProductImages(imageUrlList, imageFaceMap),
        product_desc: videoDesc,
        duration: settings.duration,
        language: settings.language,
        message_id: props?.msgContent?.message_id || undefined,
        script_mode: settings.script_mode,
        ratio: settings.ratio
      },
      opt: {
        onConnect() {
          setSubmitDisabled(true)
        },
        onBusinessError() {
          setSubmitDisabled(false)
        },
        onData(data) {
          if (data?.event_type === 'text' || data?.event_type === 'intention') {
            saveUserTextToChatPanel('请帮我生成视频')
          }
        }
      }
    })
  }

  const onOptimizeCreativeDesc = async () => {
    if (submitDisabled || optimizeLoading) return
    setOptimizeLoading(true)
    reGeneDescCreativeAPI({
      desc: videoDesc,
      images: imageUrlList,
      language: settings.language,
      duration: settings.duration
    })
      .then(res => {
        if (res?.code === 0 && res?.data?.desc != null) {
          setVideoDesc(res.data.desc)
        }
      })
      .catch(err => {
        console.error('err', err)
        message.error('优化失败')
      })
      .finally(() => {
        setOptimizeLoading(false)
      })
  }

  const uploadImages = async (value: string | string[]) => {
    const nextUrls = Array.from(new Set(normalizeImageUrls(value)))
    /** CosUpload 多图模式会按 [A]、[A,B] 这类累计列表连续回调，这里只处理本次新增的 URL */
    const previousUploadUrlSet = new Set(uploadValueListRef.current)
    const uploadUrls = nextUrls.filter(
      url => !previousUploadUrlSet.has(url) && !pendingUploadUrlSetRef.current.has(url)
    )
    uploadValueListRef.current = nextUrls

    if (uploadUrls.length === 0) {
      return
    }

    const restCount =
      MAX_REFERENCE_IMAGES - imageUrlListRef.current.length - pendingUploadUrlSetRef.current.size
    const urls = uploadUrls.slice(0, restCount)
    if (urls.length === 0) {
      message.warning(`最多上传 ${MAX_REFERENCE_IMAGES} 张参考图片`)
      return
    }

    urls.forEach(url => pendingUploadUrlSetRef.current.add(url))
    try {
      message.loading({ key: UPLOAD_MESSAGE_KEY, content: '上传中...', duration: 0 })

      /** 多张图片独立检测人脸，允许部分成功，所以用 allSettled 而不是 Promise.all */
      const results = await Promise.allSettled(
        urls.map(async imageUrl => {
          const res = await reuploadImageAPI({
            message_id: props?.msgContent?.message_id || '',
            image_url: imageUrl,
            origin_image_url: imageUrl
          })

          if (res?.code !== 0) {
            throw new Error(res?.message || '上传失败')
          }

          return {
            imageUrl,
            hasFace: Boolean(res?.data?.has_face)
          }
        })
      )

      const acceptedImages = results.flatMap(result =>
        result.status === 'fulfilled' ? [result.value] : []
      )
      const acceptedFaceMap = acceptedImages.reduce<Record<string, boolean>>(
        (faceMap, item) => ({
          ...faceMap,
          [item.imageUrl]: item.hasFace
        }),
        {}
      )
      const failedCount = results.length - acceptedImages.length
      const lastErrorMessage =
        [...results]
          .reverse()
          .find((result): result is PromiseRejectedResult => result.status === 'rejected')?.reason
          ?.message || ''

      message.destroy(UPLOAD_MESSAGE_KEY)
      if (acceptedImages.length > 0) {
        const nextImageUrlList = [...imageUrlListRef.current]
        acceptedImages.forEach(({ imageUrl }) => {
          if (
            !nextImageUrlList.includes(imageUrl) &&
            nextImageUrlList.length < MAX_REFERENCE_IMAGES
          ) {
            nextImageUrlList.push(imageUrl)
          }
        })
        imageUrlListRef.current = nextImageUrlList
        setImageUrlList(nextImageUrlList)
        setImageFaceMap(prev => {
          const nextImageFaceMap = { ...prev, ...acceptedFaceMap }
          setHasFace(getHasFace(nextImageUrlList, nextImageFaceMap))
          return nextImageFaceMap
        })
        if (failedCount > 0) {
          message.warning(`部分图片上传失败，已成功上传 ${acceptedImages.length} 张`)
        } else {
          message.success('上传成功')
        }
      } else {
        message.error(lastErrorMessage || '上传失败')
      }
    } catch (err) {
      console.error('err', err)
      message.destroy(UPLOAD_MESSAGE_KEY)
      message.error('上传失败')
    } finally {
      urls.forEach(url => pendingUploadUrlSetRef.current.delete(url))
    }
  }

  const removeImage = (index: number) => {
    if (submitDisabled) {
      return
    }
    const removedUrl = imageUrlList[index]
    const nextImageUrlList = imageUrlList.filter((_, itemIndex) => itemIndex !== index)
    setImageUrlList(prev => {
      const next = prev.filter((_, itemIndex) => itemIndex !== index)
      imageUrlListRef.current = next
      uploadValueListRef.current = next
      return next
    })
    const nextImageFaceMap = { ...imageFaceMap }
    if (removedUrl) {
      delete nextImageFaceMap[removedUrl]
    }
    setImageFaceMap(prev => {
      if (!removedUrl) {
        return prev
      }
      const rest = { ...prev }
      delete rest[removedUrl]
      return rest
    })
    setHasFace(getHasFace(nextImageUrlList, nextImageFaceMap))
    setDeleteHoverIndex(null)
  }

  /** 一键生成商品图：无模型选择后固定处理第一张参考图 */
  const handleRegeneReferenceImage = async () => {
    if (submitDisabled) {
      return
    }
    const targetImageIndex = 0
    const targetImageUrl = imageUrlList[targetImageIndex]
    if (!targetImageUrl?.trim()) {
      message.error('暂无参考图')
      return
    }
    try {
      /** duration: 0 避免沿用全局默认 3s 自动关闭，须配合 finally 里 message.destroy */
      message.loading('正在生成商品图...', 0)
      const res = await regeneImageAPI({ image_url: targetImageUrl })
      if (res?.code === 0 && res.data?.image_url) {
        const newImageUrl = res?.data?.image_url
        const reuploadRes = await reuploadImageAPI({
          message_id: props?.msgContent?.message_id || '',
          image_url: newImageUrl ?? '',
          origin_image_url: targetImageUrl
        })

        if (reuploadRes?.code === 0 && newImageUrl) {
          const newImageHasFace = Boolean(reuploadRes?.data?.has_face)
          const nextImageUrlList = imageUrlList.map((url, index) =>
            index === targetImageIndex ? newImageUrl : url
          )
          const nextImageFaceMap = { ...imageFaceMap }
          delete nextImageFaceMap[targetImageUrl]
          nextImageFaceMap[newImageUrl] = newImageHasFace
          setImageUrlList(prev => {
            const next = [...prev]
            next[targetImageIndex] = newImageUrl
            imageUrlListRef.current = next
            uploadValueListRef.current = next
            return next
          })
          setImageFaceMap(prev => {
            const rest = { ...prev }
            delete rest[targetImageUrl]
            return {
              ...rest,
              [newImageUrl]: newImageHasFace
            }
          })
          setHasFace(getHasFace(nextImageUrlList, nextImageFaceMap))
          message.success('一键生成商品图成功')
        }
      }
      if (res?.message && res.code !== 0) {
        message.error(res.message)
      }
    } catch (err) {
      console.error('err', err)
      message.error('生成失败')
    } finally {
      message.destroy()
    }
  }

  const renderControl = (params: {
    popoverKey: PopoverKey
    icon: React.ReactNode
    label: string
    value: React.ReactNode
    className?: string
    content: React.ReactNode
  }) => {
    const active = openPopover === params.popoverKey

    return (
      <Popover
        open={active}
        trigger="click"
        placement="bottomLeft"
        arrow={false}
        overlayClassName={styles.settingPopoverOverlay}
        content={params.content}
        onOpenChange={open => {
          if (submitDisabled) {
            setOpenPopover(null)
            return
          }
          setOpenPopover(open ? params.popoverKey : null)
        }}
      >
        <button
          type="button"
          disabled={submitDisabled}
          className={cn(styles.settingTrigger, params.className, {
            [styles.settingTriggerActive]: active,
            [styles.settingTriggerDisabled]: submitDisabled
          })}
        >
          <span className={styles.settingTriggerMeta}>
            <span className={styles.settingTriggerIcon}>{params.icon}</span>
            <span className={styles.settingTriggerLabel}>{params.label}</span>
          </span>
          <span className={styles.settingTriggerValue}>{params.value}</span>
          <DownOutlined className={styles.settingTriggerArrow} />
        </button>
      </Popover>
    )
  }

  const specPopoverContent = (
    <div className={styles.specOptions}>
      <div className={styles.optionGroup}>
        <div className={styles.optionGroupTitle}>视频比例</div>
        <div className={styles.optionChips}>
          {ASPECT_RATIO_OPTIONS.map(item => (
            <button
              key={item.value}
              type="button"
              className={cn(styles.optionChip, {
                [styles.optionChipActive]: settings.ratio === item.value
              })}
              onClick={() => updateSettings({ ratio: item.value })}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
      <div className={styles.optionGroup}>
        <div className={styles.optionGroupTitle}>视频时长</div>
        <div className={styles.optionChips}>
          {DURATION_OPTIONS.map(duration => (
            <button
              key={duration}
              type="button"
              className={cn(styles.optionChip, {
                [styles.optionChipActive]: settings.duration === duration
              })}
              onClick={() => updateSettings({ duration })}
            >
              {duration}s
            </button>
          ))}
        </div>
      </div>
    </div>
  )

  const languagePopoverContent = (
    <div className={styles.languageOptions}>
      <div className={styles.optionGroupTitle}>视频语言</div>
      <div className={styles.languageChips}>
        {VIDEO_LANGUAGE_OPTIONS.map(item => (
          <button
            key={item.value}
            type="button"
            className={cn(styles.languageChip, {
              [styles.optionChipActive]: settings.language === item.value
            })}
            onClick={() => {
              updateSettings({ language: item.value })
              setCustomLanguageInput('')
              setOpenPopover(null)
            }}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div className={styles.manualLanguageRow}>
        <Input
          bordered={false}
          value={customLanguageInput}
          className={styles.manualLanguageInput}
          placeholder="手动输入"
          prefix={<EditOutlined className={styles.manualLanguageIcon} />}
          onChange={e => setCustomLanguageInput(e.target.value)}
          onPressEnter={() => {
            const nextLanguage = customLanguageInput.trim()
            if (nextLanguage) {
              updateSettings({ language: nextLanguage })
              setOpenPopover(null)
            }
          }}
        />
        <button
          type="button"
          className={styles.manualLanguageConfirm}
          onClick={() => {
            const nextLanguage = customLanguageInput.trim()
            if (nextLanguage) {
              updateSettings({ language: nextLanguage })
              setOpenPopover(null)
            }
          }}
          aria-label="确认手动输入语言"
        >
          {customLanguageInput.trim() ? (
            <CheckCircleFilled className="y-text-14" />
          ) : (
            <CheckCircleOutlined className="y-text-14" />
          )}
        </button>
      </div>
    </div>
  )

  const quantityPopoverContent = (
    <div className={styles.quantityOptions}>
      <Radio.Group
        value={settings.script_mode}
        className={styles.scriptTypeRow}
        onChange={e => updateSettings({ script_mode: e.target.value })}
      >
        {SCRIPT_TYPE_OPTIONS.map(item => (
          <Radio key={item.value} value={item.value} className={styles.scriptTypeItem}>
            {item.label}
          </Radio>
        ))}
      </Radio.Group>
      <div className={styles.quantityChips}>
        {QUANTITY_OPTIONS.map(quantity => (
          <button
            key={quantity}
            type="button"
            className={cn(styles.quantityChip, {
              [styles.optionChipActive]: settings.count === quantity
            })}
            onClick={() => {
              updateSettings({ count: quantity })
              setOpenPopover(null)
            }}
          >
            {quantity}
          </button>
        ))}
      </div>
    </div>
  )

  if (status === 1 || status === 3) {
    return (
      <>
        <SkeletonScreen
          status={status}
          title={status === 1 ? '思考中，正在为您解析商品...' : title}
        />
      </>
    )
  }
  // status 为 0 或 1 时显示 loading 动画
  const isLoading = status === 1
  const uploadDisabled = submitDisabled || imageUrlList.length >= MAX_REFERENCE_IMAGES

  return (
    <Card className={styles.videoCard}>
      <div className={styles.header}>
        {isLoading ? (
          <div className={styles.icon}>
            <LottieLoading width={20} height={20} />
          </div>
        ) : (
          <img className={styles.icon} src={statusMap[2]} alt="icon" />
        )}
        <span className={styles.title}>视频设置</span>
      </div>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>
            <span className={styles.required}>*</span>
            参考图片
          </span>
          <CosUpload
            useDraggerUpload={false}
            disabled={uploadDisabled}
            onChange={uploadImages}
            showDeleteBtn={false}
            multiple
            maxCount={MAX_REFERENCE_IMAGES}
            value={imageUrlList}
            showSuccessMessage={false}
          >
            <span
              className={cn(styles.uploadLink, {
                [styles.uploadLinkDisabled]: uploadDisabled
              })}
            >
              上传图片
            </span>
          </CosUpload>
        </div>
        {imageUrlList.length > 0 ? (
          <div className={styles.refImageList}>
            {imageUrlList.map((imageUrl, index) => {
              const deleteHovered = deleteHoverIndex === index
              return (
                <div
                  key={`${imageUrl}-${index}`}
                  className={cn(styles.refImageItem, {
                    [styles.refImageItemOverlayVisible]: deleteHovered
                  })}
                >
                  <Image
                    src={imageUrl}
                    alt="参考图"
                    rootClassName={styles.refImageRoot}
                    preview={{
                      maskClosable: true,
                      mask: <span className={styles.previewMaskText}>预览</span>
                    }}
                  />
                  <span className={styles.refImageOverlayText}>{deleteHovered ? '删除' : ''}</span>
                  <button
                    type="button"
                    disabled={submitDisabled}
                    className={styles.deleteImageBtn}
                    onClick={e => {
                      e.stopPropagation()
                      removeImage(index)
                    }}
                    onMouseEnter={() => setDeleteHoverIndex(index)}
                    onMouseLeave={() => setDeleteHoverIndex(null)}
                    aria-label="删除图片"
                  >
                    <CloseOutlined />
                  </button>
                </div>
              )
            })}
          </div>
        ) : (
          <div className={styles.refImagePlaceholder}>
            <FileImageOutlined className={styles.placeholderIcon} />
            <span>暂无参考图</span>
          </div>
        )}
      </section>

      {hasFace ? (
        <div className={styles.warningRow}>
          <span className={styles.warningText}>
            商品图中含有人脸可能会导致视频生成效果，或者试试
          </span>
          <div
            className={cn(styles.warningLink, {
              [styles.warningLinkDisabled]: submitDisabled
            })}
            onClick={handleRegeneReferenceImage}
          >
            一键生成商品图
          </div>
          重新进行生成
        </div>
      ) : null}

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>
            <span className={styles.required}>*</span>
            创意描述
          </span>
          <div className={styles.sectionActions}>
            <div
              className={cn(styles.aiOptBtn, optimizeLoading && styles.aiOptBtnLoading)}
              onClick={onOptimizeCreativeDesc}
              role="button"
              tabIndex={0}
              onKeyDown={e => {
                if ((e.key === 'Enter' || e.key === ' ') && !optimizeLoading) {
                  e.preventDefault()
                  onOptimizeCreativeDesc()
                }
              }}
              aria-disabled={optimizeLoading}
            >
              {optimizeLoading ? (
                <Spin size="small" className={styles.aiOptSpinner} />
              ) : (
                <img src={penSvg} alt="" className={styles.aiOptIcon} />
              )}
              <span className={styles.aiOptText}>AI优化</span>
            </div>
          </div>
        </div>
        <Input.TextArea
          disabled={submitDisabled}
          value={videoDesc}
          onChange={e => setVideoDesc(e.target.value)}
          placeholder={CREATIVE_PLACEHOLDER}
          rows={7}
          maxLength={maxCreativeLen}
          showCount={{ formatter: ({ count, maxLength }) => `${count}/${maxLength}字` }}
          className={styles.creativeTextArea}
        />
      </section>

      <section className={styles.section}>
        <h3 className={styles.subTitle}>
          <span className={styles.required}>*</span>
          生成设置
        </h3>
        <Form form={form} disabled={submitDisabled} component={false} initialValues={settings}>
          <Form.Item name="ratio" hidden rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="duration" hidden rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="language" hidden rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="count" hidden rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="script_mode" hidden rules={[{ required: true }]}>
            <Input />
          </Form.Item>
        </Form>
        <div className={styles.settingBar}>
          {renderControl({
            popoverKey: 'spec',
            icon: <SettingOutlined />,
            label: '视频规格',
            value: `${settings.ratio} · ${settings.duration}s`,
            className: styles.specTrigger,
            content: specPopoverContent
          })}
          {renderControl({
            popoverKey: 'language',
            icon: <GlobalOutlined />,
            label: '',
            value: getLanguageLabel(settings.language),
            className: styles.languageTrigger,
            content: languagePopoverContent
          })}
          {renderControl({
            popoverKey: 'quantity',
            icon:
              settings.script_mode === 'variant_script' ? (
                <FileAddOutlined />
              ) : (
                <BranchesOutlined />
              ),
            label: '',
            value: `生成 ${settings.count} 条`,
            className: styles.quantityTrigger,
            content: quantityPopoverContent
          })}
        </div>
      </section>

      <div>
        <Button
          disabled={submitDisabled}
          onClick={onSubmit}
          icon={<img src={genVideoSvg} alt="" />}
        >
          <span>生成AI视频</span>
        </Button>
      </div>
    </Card>
  )
}

export default observer(VideoDescCard)
